/**
 * Query cache time constants for TanStack Query
 * All times are in milliseconds
 */

// Base time units
export const MINUTE = 60 * 1000;
export const HOUR = 60 * MINUTE;
export const DAY = 24 * HOUR;

// Stale time constants (how long data is considered fresh)
export const STALE_TIME = {
  // Categories don't change often, so they can be cached longer
  CATEGORIES: 15 * MINUTE, // 15 minutes
  CATEGORIES_ORG: 10 * MINUTE, // 10 minutes for org categories

  // User data might change more frequently
  USER: 5 * MINUTE, // 5 minutes

  // Benefits and other dynamic content
  BENEFITS: 10 * MINUTE, // 10 minutes

  // Organisation data
  ORGANISATION: 5 * MINUTE, // 5 minutes
  ORGANISATION_FEED: 2 * MINUTE, // 2 minutes - feed data changes more frequently
  ORGANISATION_POPULAR: 10 * MINUTE, // 10 minutes - popular content changes less frequently

  // Subscriptions
  SUBSCRIPTIONS: 2 * MINUTE, // 2 minutes

  // Receipts
  RECEIPTS: 2 * MINUTE, // 2 minutes

  // Merchant data
  MERCHANT: 5 * MINUTE, // 5 minutes
  MERCHANT_FEED: 2 * MINUTE, // 2 minutes - merchant feed data changes frequently

  // Settings and configuration
  SETTINGS: 30 * MINUTE, // 30 minutes

  // Real-time data that changes frequently
  NOTIFICATIONS: 2 * MINUTE, // 2 minutes - notifications change frequently
  NOTIFICATIONS_DETAIL: 5 * MINUTE, // 5 minutes - individual notification details

  // Static data that rarely changes
  STATIC: 1 * HOUR // 1 hour
} as const;

// Garbage collection time constants (how long to keep data in cache)
export const GC_TIME = {
  // Categories can be kept in cache longer since they don't change often
  CATEGORIES: 30 * MINUTE, // 30 minutes
  CATEGORIES_ORG: 15 * MINUTE, // 15 minutes for org categories

  // User data
  USER: 10 * MINUTE, // 10 minutes

  // Benefits and dynamic content
  BENEFITS: 15 * MINUTE, // 15 minutes

  // Organisation data
  ORGANISATION: 10 * MINUTE, // 10 minutes
  ORGANISATION_FEED: 5 * MINUTE, // 5 minutes
  ORGANISATION_POPULAR: 15 * MINUTE, // 15 minutes

  // Subscriptions
  SUBSCRIPTIONS: 5 * MINUTE, // 5 minutes

  // Receipts
  RECEIPTS: 5 * MINUTE, // 5 minutes

  // Merchant data
  MERCHANT: 10 * MINUTE, // 10 minutes
  MERCHANT_FEED: 5 * MINUTE, // 5 minutes

  // Settings
  SETTINGS: 1 * HOUR, // 1 hour

  // Notifications
  NOTIFICATIONS: 5 * MINUTE, // 5 minutes
  NOTIFICATIONS_DETAIL: 10 * MINUTE, // 10 minutes

  // Static data
  STATIC: 2 * HOUR // 2 hours
} as const;

// Prefetch time constants (for prefetching data)
export const PREFETCH_TIME = {
  // Categories can be prefetched for a long time
  CATEGORIES: 1 * DAY, // 1 day

  // Other data
  DEFAULT: 1 * HOUR // 1 hour
} as const;

// Query key suffix constants for consistent naming
export const QUERY_KEY_SUFFIXES = {
  // Settings related
  SETTINGS: 'settings',
  MERCHANT_SETTINGS: 'merchant-settings',
  ORGANISATION_SETTINGS: 'organisation-settings',
  WEBSHOP_SETTINGS: 'webshop-settings',

  // User related
  USER_SETTINGS: 'user-settings',
  USER_PROFILE: 'profile',
  USER_STATISTICS: 'statistics',

  // Categories related
  CATEGORIES: 'categories',
  B2C_CATEGORIES: 'b2c-categories',
  ORG_CATEGORIES: 'org-categories',
  BENEFIT_CATEGORIES: 'benefit-categories',

  // Benefits related
  BENEFITS: 'benefits',
  USER_BENEFITS: 'user-benefits',

  // Notifications
  NOTIFICATIONS: 'notifications',
  UNREAD_NOTIFICATIONS: 'unread-notifications',

  // Subscriptions
  SUBSCRIPTIONS: 'subscriptions',
  ACTIVE_SUBSCRIPTIONS: 'active-subscriptions',

  // Organisations
  ORGANISATIONS: 'organisations',
  PENDING_ORGANISATIONS: 'pending-organisations',

  // Merchants
  MERCHANTS: 'merchants',
  MERCHANT_DATA: 'merchant-data'
} as const;
